/**
 * The media plane's log sink.
 *
 * Every other failure in apps/web is reported through the UI — a toast, an
 * inline error state, the degraded-quality banner — because every other failure
 * is one the user can see. The media plane has a class of fault that nobody can
 * see: a track that publishes successfully and merely looks wrong, a codec the
 * SFU quietly substituted, a constraint the OS declined to honour. Those have
 * to go somewhere that is NOT the user's screen, or they go nowhere at all.
 *
 * Deliberately not a logging framework. Two levels, because there are exactly
 * two questions worth answering: "is this worth a developer's attention while
 * they are working?" and "did this happen at all, in production, even once?".
 */

const PREFIX = '[media]';

/** Conditions already surfaced by `reportOnce`, keyed by call site. */
const reported = new Set<string>();

const emit = (write: (...args: unknown[]) => void, message: string, detail?: unknown): void => {
  if (detail === undefined) write(`${PREFIX} ${message}`);
  else write(`${PREFIX} ${message}`, detail);
};

export const mediaLog = {
  /**
   * A developer-facing note. Silent in production on purpose: a console that
   * scrolls in every session is what trains people to ignore the one line that
   * mattered.
   */
  warn(message: string, detail?: unknown): void {
    if (!import.meta.env.DEV) return;
    emit(console.warn, message, detail);
  },

  /**
   * A fault that is invisible in the product and must therefore be visible
   * here — in production too, which is the only place some of them can happen.
   *
   * Reported ONCE per `key` per page load: these conditions are properties of
   * the session (a server codec list, a missing capability), so the second
   * occurrence carries nothing the first did not, and a repeat on every publish
   * would bury it in its own noise.
   */
  reportOnce(key: string, message: string, detail?: unknown): void {
    if (reported.has(key)) return;
    reported.add(key);
    emit(console.error, message, detail);
  },
};
